import { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

// Import Css
import styles from "../forms.module.scss";

// Import custom functions
import handleClassName from "@/utils/handleClassName";
import submitForm from "@/utils/Forms/submitForm";

// Import types and interface
import FormType from "@/interfaces/Forms/FormType";

function ContactForm({ formFieldsMap, formID }) {
  const [isFormSuccessfullySend, setIsformSuccesfullySend] = useState(false);
  const [isFormGotError, setIsformGotError] = useState(false);
  const [formMessage, setFormMessage] = useState("");

  const validationRequired = "Ce champ est requis";
  const phoneRegex = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/;
  const initialValues: FormType = {
    entreprise: "",
    projet: "",
    email: "",
    phone: "",
    budget: "",
    description: "",
    name: "",
  };

  return (
    <div className={handleClassName(["forms"], styles)}>
      <Formik
        initialValues={initialValues}
        validationSchema={Yup.object({
          entreprise: Yup.string()
            .max(30, "Doit comporter 15 caractères ou moins")
            .required(validationRequired),
          projet: Yup.string()
            .oneOf(
              ["web", "marketing", "social", "app", "un-peu-de-tout"],
              "Type de projet non valide"
            )
            .required(validationRequired),
          email: Yup.string()
            .email("Adresse courriel non valide")
            .required(validationRequired),
          phone: Yup.string()
            .matches(
              phoneRegex,
              "Veuillez vérifier le formatage du numéro de téléphone. (EX: 123 444-5555)"
            )
            .required(validationRequired),
          budget: Yup.string()
            .oneOf(
              ["1-filiale", "2-12-filiales", "13-et-plus-filiales"],
              "Nombre de filiale non valide"
            )
            .required(validationRequired),
          name: Yup.string().oneOf([""]),
        })}
        onSubmit={async (values) => {
          if (!isFormSuccessfullySend) {
            let formData = new FormData();

            Object.entries(values).forEach((e) => {
              if (e[1].length !== 0 && e[1].length !== undefined) {
                if (e[0] === "fileUpload") {
                  formData.append(formFieldsMap[e[0]], e[1], e[1].name);
                } else {
                  formData.append(formFieldsMap[e[0]], e[1]);
                }
              }
            });

            formData.append("formID", formID);

            const response = await submitForm(formData);

            if (response.code === 200) {
              setIsformSuccesfullySend(true);
              setIsformGotError(false);
              setFormMessage(
                "Merci de nous avoir contactés ! Nous vous répondrons dans les plus brefs délais."
              );
            } else {
              setIsformGotError(true);
              setFormMessage(response.message);
            }
          }
        }}
      >
        {({ values, setFieldValue, isSubmitting, touched }) => (
          <Form>
            <div className={styles["forms--cell"]}>
              <ErrorMessage
                className={styles["forms--errors"]}
                name="entreprise"
                component="div"
              />
              <Field
                type="entreprise"
                name="entreprise"
                placeholder="Votre nom d'entreprise"
                onChange={(e) => {
                  if (isFormSuccessfullySend) setIsformSuccesfullySend(false);
                  setFieldValue("entreprise", e.target.value);
                }}
              />
              <label htmlFor="entreprise">Nom de l&#39;entreprise</label>
            </div>

            <div className={styles["forms--cell"]}>
              <ErrorMessage
                className={styles["forms--errors"]}
                name="projet"
                component="div"
              />

              <div className={styles["forms--select"]}>
                <Field
                  type="projet"
                  as="select"
                  name="projet"
                  onChange={(e) => {
                    if (isFormSuccessfullySend) setIsformSuccesfullySend(false);
                    setFieldValue("projet", e.target.value);
                  }}
                >
                  <option value="">Choisir un service</option>
                  <option value="marketing">Marketing Web</option>
                  <option value="web">Conception Web</option>
                  <option value="social">Réseaux Sociaux</option>
                  <option value="un-peu-de-tout">Un peu de tout!</option>
                </Field>
              </div>

              <label htmlFor="projet">Type de projet</label>
            </div>

            <div className={styles["forms--cell"]}>
              <ErrorMessage
                className={styles["forms--errors"]}
                name="email"
                component="div"
              />
              <Field
                type="email"
                name="email"
                placeholder="example@example.com"
                onChange={(e) => {
                  if (isFormSuccessfullySend) setIsformSuccesfullySend(false);
                  setFieldValue("email", e.target.value);
                }}
              />
              <label htmlFor="email">Votre courriel</label>
            </div>

            <div className={styles["forms--cell"]}>
              <ErrorMessage
                className={styles["forms--errors"]}
                name="phone"
                component="div"
              />
              <Field
                type="phone"
                name="phone"
                placeholder="450 555-5555"
                onChange={(e) => {
                  if (isFormSuccessfullySend) setIsformSuccesfullySend(false);
                  setFieldValue("phone", e.target.value);
                }}
              />
              <label htmlFor="phone">Téléphone</label>
            </div>

            <div
              className={handleClassName(["forms--cell", "full-width"], styles)}
            >
              <ErrorMessage
                className={styles["forms--errors"]}
                name="budget"
                component="div"
              />

              <div
                role="group"
                className={styles["forms--filiales"]}
                aria-labelledby="my-radio-group"
              >
                <label
                  className={handleClassName(
                    [
                      "forms--radio-btn",
                      values.budget === "1-filiale" && "selected",
                    ],
                    styles
                  )}
                >
                  <Field
                    type="radio"
                    name="budget"
                    value="1-filiale"
                    onChange={(e) => {
                      if (isFormSuccessfullySend)
                        setIsformSuccesfullySend(false);
                      setFieldValue("budget", e.target.value);
                    }}
                  />
                  1
                </label>

                <label
                  className={handleClassName(
                    [
                      "forms--radio-btn",
                      values.budget === "2-12-filiales" && "selected",
                    ],
                    styles
                  )}
                >
                  <Field
                    type="radio"
                    name="budget"
                    value="2-12-filiales"
                    onChange={(e) => {
                      if (isFormSuccessfullySend)
                        setIsformSuccesfullySend(false);
                      setFieldValue("budget", e.target.value);
                    }}
                  />
                  2 à 12
                </label>

                <label
                  className={handleClassName(
                    [
                      "forms--radio-btn",
                      values.budget === "13-et-plus-filiales" && "selected",
                    ],
                    styles
                  )}
                >
                  <Field
                    type="radio"
                    name="budget"
                    value="13-et-plus-filiales"
                    onChange={(e) => {
                      if (isFormSuccessfullySend)
                        setIsformSuccesfullySend(false);
                      setFieldValue("budget", e.target.value);
                    }}
                  />
                  13 et plus
                </label>
              </div>

              <label id="my-radio-group">Nombre de place d&#39;affaires?</label>
            </div>

            <div
              className={handleClassName(["forms--cell", "full-width"], styles)}
            >
              <ErrorMessage
                className={styles["forms--errors"]}
                name="description"
                component="div"
              />
              <Field
                type="description"
                as="textarea"
                name="description"
                placeholder="Donnez-nous les détails!"
                rows="3"
                onChange={(e) => {
                  if (isFormSuccessfullySend) setIsformSuccesfullySend(false);
                  setFieldValue("description", e.target.value);
                }}
              />
              <label htmlFor="description">Parlez-nous du projet</label>
            </div>

            <div className="sr-only">
              <Field
                type="name"
                name="name"
                placeholder="ton nom ici"
                autoComplete={"off"}
                onChange={(e) => {
                  if (isFormSuccessfullySend) setIsformSuccesfullySend(false);
                  setFieldValue("name", e.target.value);
                }}
              />
              <label htmlFor="name"></label>
            </div>

            <button
              className={handleClassName(
                [
                  "forms--submit",
                  isFormSuccessfullySend
                    ? "forms--success"
                    : "forms--not-submited",
                ],
                styles
              )}
              type="submit"
              disabled={isSubmitting}
            >
              <div className={styles["inner-btn"]}>
                {isFormSuccessfullySend
                  ? "Envoyé avec success"
                  : "Envoyez votre projet"}
              </div>
            </button>

            <div
              className={handleClassName(
                [
                  "forms--submit-message",
                  isFormGotError ? "forms--errors" : "",
                  isFormGotError || isFormSuccessfullySend ? "visible" : "",
                ],
                styles
              )}
            >
              <p>{formMessage}</p>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default ContactForm;
